import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Section } from '@/components/manager/overview/Section'
import {
  useChangeSubscription,
  usePreviewChangeSubscription,
} from '@/api/stripe'
import type { Subscription, SubscriptionPlan } from '@/api/subscription'

interface Props {
  subscription: Subscription
  darkMode?: boolean
}

/**
 * Permet de basculer entre Starter et Pro dans le même billing period
 * (monthly/yearly). La proration Stripe est calculée en preview, puis
 * affichée dans la confirmation pour transparence. Une charge/crédit
 * immédiat est appliqué selon le sens du switch.
 *
 * Hors scope v1 : changer de billing period (monthly→yearly), ajouter/
 * retirer toll-free addon. Ces actions resteront sur Stripe Portal ou
 * une feature dédiée plus tard.
 */
export function ChangePlanCard({ subscription, darkMode = false }: Props) {
  const { t, i18n } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()

  const currentPlan = subscription.plan
  // On ne propose le switch que pour starter↔pro (pas trial / toll_free)
  if (currentPlan !== 'starter' && currentPlan !== 'pro') return null

  const targetPlan: SubscriptionPlan = currentPlan === 'starter' ? 'pro' : 'starter'
  const isUpgrade = targetPlan === 'pro'

  return (
    <Section
      title={t('profile.billing.changePlanTitle', 'Changer de plan')}
      icon={<Sparkles className="h-5 w-5" />}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {isUpgrade
              ? t(
                  'profile.billing.upgradeHint',
                  'Passez à Pro pour débloquer enregistrement, transcription et résumés IA.',
                )
              : t(
                  'profile.billing.downgradeHint',
                  'Revenir à Starter - perte de l\'enregistrement, transcription et résumés IA.',
                )}
          </p>
        </div>
        <ChangePlanButton
          targetPlan={targetPlan}
          isUpgrade={isUpgrade}
          darkMode={darkMode}
          locale={locale || i18n.language}
        />
      </div>
    </Section>
  )
}

interface ButtonProps {
  targetPlan: 'starter' | 'pro'
  isUpgrade: boolean
  darkMode: boolean
  locale: string
}

function ChangePlanButton({ targetPlan, isUpgrade, darkMode, locale }: ButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const previewMutation = usePreviewChangeSubscription()
  const changeMutation = useChangeSubscription()

  // Quand on ouvre, on déclenche le preview (1 seule fois)
  useEffect(() => {
    if (!open || previewMutation.isPending || previewMutation.data) return
    previewMutation.mutate(targetPlan, {
      onError: () =>
        toast.error(
          t('profile.billing.previewError', 'Impossible de calculer la facture.'),
        ),
    })
  }, [open, previewMutation, targetPlan, t])

  // Reset à la fermeture (le hook mutation garde data sinon)
  useEffect(() => {
    if (!open) previewMutation.reset()
  }, [open, previewMutation])

  const handleConfirm = () => {
    changeMutation.mutate(targetPlan, {
      onSuccess: () => {
        toast.success(
          isUpgrade
            ? t('profile.billing.upgradeSuccess', 'Plan mis à niveau')
            : t('profile.billing.downgradeSuccess', 'Plan modifié'),
        )
        setOpen(false)
      },
      onError: () =>
        toast.error(t('profile.billing.changeError', 'Impossible de changer de plan.')),
    })
  }

  const planLabel = targetPlan === 'pro' ? 'Pro' : 'Starter'
  const TrendIcon = isUpgrade ? ArrowUp : ArrowDown

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant={isUpgrade ? 'default' : 'outline'}
        onClick={() => setOpen(true)}
        className={cn(
          'flex-shrink-0',
          isUpgrade && 'bg-blue-600 hover:bg-blue-700',
          !isUpgrade && darkMode && 'border-slate-700',
        )}
      >
        <TrendIcon className="h-4 w-4 mr-2" />
        {isUpgrade
          ? t('profile.billing.upgradeTo', 'Passer à {{plan}}', { plan: planLabel })
          : t('profile.billing.downgradeTo', 'Revenir à {{plan}}', { plan: planLabel })}
      </Button>
      <AlertDialogContent
        className={cn(darkMode && 'bg-slate-900 border-slate-700')}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className={darkMode ? 'text-white' : ''}>
            {isUpgrade
              ? t('profile.billing.confirmUpgradeTitle', 'Passer à {{plan}} ?', {
                  plan: planLabel,
                })
              : t('profile.billing.confirmDowngradeTitle', 'Revenir à {{plan}} ?', {
                  plan: planLabel,
                })}
          </AlertDialogTitle>
          <AlertDialogDescription
            className={cn('space-y-2', darkMode && 'text-slate-400')}
          >
            {previewMutation.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('profile.billing.previewLoading', 'Calcul de la facture…')}
              </span>
            ) : previewMutation.data ? (
              <ProrationBreakdown
                amount={previewMutation.data.immediate_total}
                currency={previewMutation.data.currency}
                locale={locale}
                isUpgrade={isUpgrade}
                isDowngrade={previewMutation.data.is_downgrade === true}
                isTrial={previewMutation.data.is_trial === true}
                effectiveAt={previewMutation.data.effective_at ?? null}
                darkMode={darkMode}
              />
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className={darkMode ? 'border-slate-700' : ''}
            disabled={changeMutation.isPending}
          >
            {t('common.cancel', 'Annuler')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={changeMutation.isPending || previewMutation.isPending}
            className={
              isUpgrade
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-amber-600 hover:bg-amber-700'
            }
          >
            {changeMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            {t('profile.billing.changeConfirm', 'Confirmer')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ProrationProps {
  amount: number // cents
  currency: string
  locale: string
  isUpgrade: boolean
  isDowngrade: boolean
  isTrial: boolean
  effectiveAt: number | null
  darkMode: boolean
}

function ProrationBreakdown({
  amount,
  currency,
  locale,
  isUpgrade,
  isDowngrade,
  isTrial,
  effectiveAt,
  darkMode,
}: ProrationProps) {
  const { t } = useTranslation()
  const isCharge = amount > 0
  const isCredit = amount < 0
  const formatted = formatAmount(Math.abs(amount) / 100, currency, locale)

  const effectiveDate =
    (isDowngrade || isTrial) && effectiveAt
      ? new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date(effectiveAt * 1000))
      : null

  // En mode trial, AUCUN crédit/charge peu importe upgrade/downgrade. Le
  // nouveau plan prend effet à la fin de l'essai.
  const trialMessage = isTrial
    ? effectiveDate
      ? t(
          'profile.billing.trialChangeExplainDate',
          'Vous êtes en essai gratuit. Le plan {{plan}} prendra effet à la fin de votre essai le {{date}}. Aucune charge maintenant.',
          { plan: isUpgrade ? 'Pro' : 'Starter', date: effectiveDate },
        )
      : t(
          'profile.billing.trialChangeExplain',
          'Vous êtes en essai gratuit. Le nouveau plan prendra effet à la fin de votre essai. Aucune charge maintenant.',
        )
    : null

  return (
    <>
      <p>
        {trialMessage ??
          (isUpgrade
            ? t(
                'profile.billing.upgradeExplain',
                'Vous bénéficiez immédiatement des fonctionnalités Pro. La différence est prorata pour les jours restants.',
              )
            : effectiveDate
              ? t(
                  'profile.billing.downgradeExplainDate',
                  'Vous gardez les fonctionnalités Pro jusqu\'au {{date}}, puis votre abonnement bascule sur Starter. Aucun remboursement ni crédit.',
                  { date: effectiveDate },
                )
              : t(
                  'profile.billing.downgradeExplain',
                  'Votre abonnement bascule sur Starter à la fin de la période courante. Aucun remboursement ni crédit.',
                ))}
      </p>
      <div
        className={cn(
          'flex items-center justify-between p-3 rounded-lg',
          darkMode ? 'bg-slate-800' : 'bg-slate-100',
        )}
      >
        <span className={darkMode ? 'text-slate-300' : 'text-slate-700'}>
          {isCharge
            ? t('profile.billing.immediateCharge', 'Charge immédiate')
            : isCredit
              ? t('profile.billing.immediateCredit', 'Crédit immédiat')
              : t('profile.billing.noImmediateCharge', 'Aucune charge')}
        </span>
        <span
          className={cn(
            'font-semibold tabular-nums',
            isCharge
              ? 'text-slate-900 dark:text-white'
              : isCredit
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-slate-500',
          )}
        >
          {isCredit ? '−' : ''}
          {formatted}
        </span>
      </div>
    </>
  )
}

function formatAmount(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`
  }
}
